import { __ } from "@wordpress/i18n";
import { 
  useBlockProps, 
  InspectorControls
} from "@wordpress/block-editor";
import { 
  PanelBody,
  TextControl,
  ToggleControl,
  Card,
  CardBody,
  Button
} from "@wordpress/components";
import { useState, useEffect } from "@wordpress/element";
import { useSelect } from "@wordpress/data";

export default function Edit({ attributes, setAttributes }) {
  const { title, selectedPosts, showDate } = attributes;
  
  const blockProps = useBlockProps({
    className: 'disaster-related-posts-block'
  });

  // Get available posts
  const { posts, isLoading } = useSelect((select) => {
    const { getEntityRecords } = select('core');
    const query = {
      per_page: 50,
      status: 'publish',
      orderby: 'date',
      order: 'desc'
    };
    
    return {
      posts: getEntityRecords('postType', 'blog', query) || [],
      isLoading: select('core/data').isResolving('core', 'getEntityRecords', ['postType', 'blog', query])
    };
  }, []);

  const addPost = (post) => {
    if (!selectedPosts.find(p => p.id === post.id)) {
      setAttributes({
        selectedPosts: [...selectedPosts, {
          id: post.id,
          title: post.title.rendered,
          excerpt: post.excerpt.rendered,
          link: post.link,
          date: post.date,
          featured_media: post.featured_media
        }]
      });
    }
  };

  const removePost = (postId) => {
    setAttributes({
      selectedPosts: selectedPosts.filter(p => p.id !== postId)
    });
  };

  return (
    <>
      <InspectorControls>
        <PanelBody title={__('Related Posts Settings', 'puroclean')}>
          <TextControl
            label={__('Section Title', 'puroclean')}
            value={title}
            onChange={(value) => setAttributes({ title: value })}
          />
          
          <ToggleControl
            label={__('Show Date', 'puroclean')}
            checked={showDate}
            onChange={(value) => setAttributes({ showDate: value })}
          />
        </PanelBody>
      </InspectorControls>

      <div {...blockProps}>
        <div className="related-posts-section mt-16">
          <h2 className="text-3xl font-bold text-secondary-900 mb-8" 
              dangerouslySetInnerHTML={{ __html: title }} />
          
          {/* Selected Posts */}
          {selectedPosts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-8">
              {selectedPosts.map((post) => (
                <Card key={post.id} className="related-post bg-white rounded-lg shadow-md overflow-hidden">
                  <CardBody className="p-6">
                    <div className="post-meta text-sm text-gray-500 mb-2">
                      {showDate && new Date(post.date).toLocaleDateString()}
                    </div>
                    
                    <h3 className="post-title text-lg font-bold mb-3 text-secondary-900">
                      <a href={post.link} className="hover:text-primary transition-colors">
                        <span dangerouslySetInnerHTML={{ __html: post.title }} />
                      </a>
                    </h3>
                    
                    {post.excerpt && (
                      <div className="post-excerpt text-gray-600 mb-4">
                        <span dangerouslySetInnerHTML={{ __html: post.excerpt }} />
                      </div>
                    )}
                    
                    <Button
                      onClick={() => removePost(post.id)}
                      variant="link"
                      isDestructive
                      size="small"
                    >
                      {__('Remove', 'puroclean')}
                    </Button>
                  </CardBody>
                </Card>
              ))}
            </div>
          )}
          
          {/* Available Posts to Add */}
          <div className="available-posts">
            <h3 className="text-lg font-semibold mb-4">Available Posts</h3>
            {isLoading ? (
              <p>{__('Loading posts...', 'puroclean')}</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {posts.map((post) => (
                  <div key={post.id} className="flex items-center justify-between p-2 border rounded">
                    <div className="flex-1">
                      <span dangerouslySetInnerHTML={{ __html: post.title.rendered }} />
                      <span className="text-sm text-gray-500 ml-2">
                        ({new Date(post.date).toLocaleDateString()})
                      </span>
                    </div>
                    <Button
                      onClick={() => addPost(post)}
                      variant="secondary"
                      size="small"
                      disabled={selectedPosts.find(p => p.id === post.id)}
                    >
                      {selectedPosts.find(p => p.id === post.id) ? 
                        __('Added', 'puroclean') : 
                        __('Add', 'puroclean')
                      }
                    </Button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
